import * as Collapsible from '@radix-ui/react-collapsible'
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, CircleCheckIcon, CircleDashedIcon, Loader2Icon, LockIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { Button, ButtonLink, TextLink } from 'slab'
import { cn } from 'utils'
import { type EmployersTodo } from 'utils/web_api.schemas'

import DueDateOrCompletionBadge from './DueDateOrCompletionBadge'
import { AchAuthorizationModal } from '../billing/modals/AchAuthorizationModal'

interface TodoItemProps {
  todo: EmployersTodo
  onMarkComplete: () => void
  isMarkingComplete: boolean
  isExpanded: boolean
  onToggleExpand: () => void
}

export function TodoItem ({ todo, onMarkComplete, isMarkingComplete, isExpanded, onToggleExpand }: TodoItemProps) {
  const isCompleted = todo.status === 'completed'
  const [openModal, setOpenModal] = useState<string | null>(null)

  const incompletePrerequisites = useMemo(() => {
    if (!todo.pre_requisite_todos) return []
    return todo.pre_requisite_todos.filter(prereq => prereq.status !== 'completed')
  }, [todo.pre_requisite_todos])

  const isLocked = incompletePrerequisites.length > 0

  return (
    <>
      <Collapsible.Root
        open={isExpanded}
        onOpenChange={onToggleExpand}
        className={cn('border-b border-color-stroke-dark transition-colors duration-200', isExpanded && 'bg-gray-100')}
      >
        <Collapsible.Trigger asChild>
          <button className={cn('w-full py-4 px-6 flex items-center gap-4 text-left transition-colors', !isExpanded && 'hover:bg-gray-50')}>
            <div
              className='flex-shrink-0'
              onClick={(e) => e.stopPropagation()}
            >
              {isCompleted
                ? <CircleCheckIcon className='bg-thatch-500 text-white rounded-full' size={20} />
                : <CircleDashedIcon className='text-gray-500' size={20} />}
            </div>
            <div className='flex-1'>
              <span data-testid='todo-item-title' className={cn('text-main body', isCompleted && 'line-through')}>{todo.title}</span>
            </div>
            <DueDateOrCompletionBadge todo={todo} />
            <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </Collapsible.Trigger>
        <Collapsible.Content className='data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close overflow-hidden'>
          <div className='px-6 pb-4 pl-14'>
            <div className='body-sm text-gray-500'>
              {isLocked && !isCompleted && (
                <div className='text-gray-500 body-sm mb-2 flex items-center gap-1'>
                  <LockIcon className='w-4 h-4 text-gray-500 ' />
                  <span>Complete above {incompletePrerequisites.length} {incompletePrerequisites.length === 1 ? 'task' : 'tasks'} to unlock</span>
                </div>
              )}
              {(!isLocked || isCompleted) && (
                <ReactMarkdown
                  components={{
                    ul: ({ children }) => (
                      <ul className='list-disc pl-5 my-2'>{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className='list-decimal pl-5 my-2'>{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li className='my-1'>{children}</li>
                    ),
                    p: ({ children }) => (
                      <p className='my-2'>{children}</p>
                    ),
                    a: ({ children, href, ...rest }) => {
                      if (isCompleted) {
                        // hide all links for completed todos
                        return <></>
                      } else if (href?.startsWith('#modal-')) {
                        const modalKey = href.replace('#modal-', '')
                        return (
                          <TextLink
                            {...rest}
                            href='#'
                            color='purple'
                            onClick={(e) => {
                              e.preventDefault()
                              setOpenModal(modalKey)
                            }}
                          >
                            {children}
                          </TextLink>
                        )
                      } else {
                        // assume all links should be buttons with a right arrow positioned below the markdown content
                        return (
                          <div className='mt-2'>
                            <ButtonLink
                              {...rest}
                              href={href || '#'}
                              variant='primary'
                              disabled={isLocked}
                            >
                              <div className='flex items-center gap-1'>
                                {children} <ArrowRightIcon className='w-4 h-4' />
                              </div>
                            </ButtonLink>
                          </div>
                        )
                      }
                    },
                  }}
                  allowedElements={['a', 'p', 'ul', 'ol', 'li']}
                >
                  {todo.body}
                </ReactMarkdown>
              )}
              {todo.self_serve_completable && !isCompleted && (
                <div className='mt-4'>
                  <Button
                    onClick={() => {
                      onMarkComplete()
                    }}
                    variant='primary'
                    disabled={isLocked || isMarkingComplete}
                  >
                    <div className='flex items-center gap-1'>
                      {isMarkingComplete ? <Loader2Icon className='w-4 h-4 text-white animate-spin' /> : <CheckIcon className='w-4 h-4 text-white' />}
                      Mark as complete
                    </div>
                  </Button>
                </div>
              )}
              {/* TODO: make Todos dismissable */}
              {/* {todo.dismissable && (
                <div className='mt-2'>
                  <ButtonLink
                    variant='tertiary-deemphasized'
                    onClick={onDismiss}
                  >
                    {todo.dismiss_cta || 'Dismiss'}
                  </ButtonLink>
                </div>
              )} */}
            </div>
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
      {openModal === 'ach_authorization' && (
        <AchAuthorizationModal
          open={openModal === 'ach_authorization'}
          onOpenChange={(open) => {
            if (!open) {
              setOpenModal(null)
            }
          }}
        />
      )}
    </>
  )
}
